/**
 * ShortcodeBlock — one paragraph (or heading) whose text may contain a
 * `{{shortcode}}` embed.
 *
 * WHY THE EMBEND IS A SIBLING AND NOT A CHILD
 * -------------------------------------------
 * An embed renders a whole module — a gallery grid, an article body, a
 * magazine card. That is BLOCK-level content. A `<p>` may only contain
 * phrasing content, and a heading may only contain phrasing content, so
 * dropping a `<section>` into either is invalid HTML: React's dev build
 * warns ("<section> cannot be a descendant of <p>") and a browser parsing
 * the markup as HTML closes the `<p>` early.
 *
 * So this component SPLITS the block at every embed. The text runs keep
 * their own `<p>` (or `<h2>`, `<h3>`); each embed becomes a SIBLING of that
 * paragraph. The reader sees the same thing; the document is valid.
 *
 *   "看相簿：{{gallery-dinner}} 歡迎出席"
 *     →  <p>看相簿：</p>
 *        <section aria-label="內嵌相簿">…</section>
 *        <p>歡迎出席</p>
 *
 * A block with NO embed renders exactly as it did before — one tag, one
 * pass of the inline renderer — so the 99% of bodies that carry no
 * shortcode are untouched.
 *
 * `<li>` and `<blockquote>` are NOT handled here: both accept flow content
 * outright, so an embed may stay inside them. Only `<p>`, `<h2>`, `<h3>`
 * and `<h4>` need the split.
 */
import type { HTMLAttributes, ReactNode } from 'react'
import ShortcodeEmbed from './ShortcodeEmbed'
import { scanShortcodes } from './shortcodes'
import { renderInlineMarkdown, type InlineMarkdownOptions } from '@/utils/inlineMarkdown'

/** The tags whose content model is phrasing-only, so a block embed must
 *  be lifted out of them. */
export type ShortcodeBlockTag = 'p' | 'h2' | 'h3' | 'h4' | 'div'

interface ShortcodeBlockProps extends HTMLAttributes<HTMLElement> {
  /** The wrapping tag for the text runs. */
  as?: ShortcodeBlockTag
  /** The block's source text — may contain `{{shortcode}}` tokens. */
  text: string
  /** Options for the inline renderer (image class, `sizes`). */
  options?: InlineMarkdownOptions
}

export default function ShortcodeBlock({
  as: Tag = 'p',
  text,
  options,
  className,
  ...rest
}: ShortcodeBlockProps) {
  const runs = scanShortcodes(text)
  const hasEmbed = runs.some((r) => typeof r !== 'string')

  // The common case: no shortcode in this block. One tag, one render —
  // byte-for-byte what this block looked like before embeds existed.
  if (!hasEmbed) {
    return (
      <Tag className={className} {...rest}>
        {renderInlineMarkdown(text, options)}
      </Tag>
    )
  }

  const out: ReactNode[] = []
  runs.forEach((run, i) => {
    if (typeof run === 'string') {
      // Whitespace-only runs between two embeds would render an empty
      // paragraph; drop them.
      if (!run.trim()) return
      out.push(
        <Tag key={`t${i}`} className={className} {...rest}>
          {renderInlineMarkdown(run, options)}
        </Tag>,
      )
      return
    }
    out.push(<ShortcodeEmbed key={`e${i}`} shortkey={run.key} />)
  })

  return <>{out}</>
}
